import React, { useState, useEffect } from "react";
import * as Slider from "@radix-ui/react-slider";
import { useFormatters } from "../../hooks/useFormatters";
import { useLanguage } from "../../contexts/LanguageContext";

const Form = ({ children, onSubmit, className = "" }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      {children}
    </form>
  );
};

const FormField = ({
  label,
  required = false,
  error,
  children,
  className = "",
  description,
}) => {
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {children}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

const FormInput = ({
  type = "text",
  placeholder,
  value,
  onChange,
  error,
  className = "",
  ...props
}) => {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      step={type === "number" ? "any" : undefined}
      className={`
        w-full border rounded-md px-3 py-2 text-sm
        focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        ${error ? "border-destructive" : "border-border"}
        bg-background text-foreground placeholder:text-muted-foreground
        [&::-webkit-calendar-picker-indicator]:cursor-pointer
        [&::-webkit-calendar-picker-indicator]:filter-[invert(32%)_sepia(96%)_saturate(1984%)_hue-rotate(204deg)_brightness(96%)_contrast(96%)]
        ${className}
      `}
      {...props}
    />
  );
};

const FormTextarea = ({
  placeholder,
  value,
  onChange,
  error,
  rows = 3,
  className = "",
  ...props
}) => {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      className={`
        w-full border rounded-md px-3 py-2 text-sm resize-none
        focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        ${error ? "border-destructive" : "border-border"}
        bg-background text-foreground placeholder:text-muted-foreground
        dark:scheme:dark
        ${className}
      `}
      {...props}
    />
  );
};

const FormSelect = ({
  options = [],
  value,
  onChange,
  error,
  placeholder,
  className = "",
  ...props
}) => {
  const { t } = useLanguage();

  return (
    <select
      value={value}
      onChange={onChange}
      className={`
        w-full border rounded-md px-3 py-2 text-sm
        focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        ${error ? "border-destructive" : "border-border"}
        bg-background text-foreground
        ${className}
      `}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const FormCheckbox = ({
  label,
  checked,
  onChange,
  error,
  className = "",
  ...props
}) => {
  return (
    <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={`
          rounded border-border text-primary focus:ring-offset-0 focus:ring-ring
          ${error ? "border-destructive" : ""}
        `}
        {...props}
      />
      <span className="text-sm font-medium text-foreground ml-2">{label}</span>
    </label>
  );
};

const FormActions = ({ children, className = "" }) => {
  return (
    <div
      className={`flex items-center justify-end gap-2 pt-4 border-t border-border ${className}`}
    >
      {children}
    </div>
  );
};

const FormSlider = ({
  firstValue,
  lastValue,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  error,
  className = "",
  ...props
}) => {

  // State for editing mode
  const [editFirst, setEditFirst] = useState(false);
  const [editLast, setEditLast] = useState(false);
  const [firstInput, setFirstInput] = useState(firstValue);
  const [lastInput, setLastInput] = useState(lastValue);

  useEffect(() => {
    setFirstInput(firstValue);
  }, [firstValue]);

  useEffect(() => {
    setLastInput(lastValue);
  }, [lastValue]);

  const { formatNumber } = useFormatters();

  // Handlers for formatted span click/edit
  const handleFirstSpanClick = () => setEditFirst(true);
  const handleLastSpanClick = () => setEditLast(true);

  const handleFirstBlur = () => {
    setEditFirst(false);
    let val = Number(firstInput);
    if (isNaN(val)) val = min;
    val = Math.max(min, Math.min(val, lastValue));
    onChange([val, lastValue]);
  };

  const handleLastBlur = () => {
    setEditLast(false);
    let val = Number(lastInput);
    if (isNaN(val)) val = max;
    val = Math.max(firstValue, Math.min(val, max));
    onChange([firstValue, val]);
  };

  const handleFirstInputChange = (e) => setFirstInput(e.target.value);
  const handleLastInputChange = (e) => setLastInput(e.target.value);

  const handleFirstInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };
  const handleLastInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <Slider.Root
        className="relative flex items-center select-none touch-none w-full h-8"
        min={min}
        max={max}
        step={step}
        value={[firstValue, lastValue]}
        onValueChange={onChange}
        {...props}
      >
        <Slider.Track className="bg-border relative grow rounded-full h-2">
          <Slider.Range className="absolute bg-primary rounded-full h-2" />
        </Slider.Track>
        <Slider.Thumb
          className={`
          block w-5 h-5 bg-background border-2 border-primary rounded-full shadow
          focus:outline-none focus:ring-2 focus:ring-ring
          ${error ? "border-destructive" : ""}
        `}
        />
        <Slider.Thumb
          className={`
          block w-5 h-5 bg-background border-2 border-primary rounded-full shadow
          focus:outline-none focus:ring-2 focus:ring-ring
          ${error ? "border-destructive" : ""}
        `}
        />
      </Slider.Root>
      <div className="flex justify-between items-center gap-2 mt-2">
        <div className="flex flex-col items-center">
          {editFirst ? (
            <FormInput
              type="number"
              className="text-xs text-muted-foreground mt-1 border rounded px-1 py-0.5 w-20"
              value={firstInput}
              autoFocus
              min={min}
              max={lastValue}
              step={step}
              onChange={handleFirstInputChange}
              onBlur={handleFirstBlur}
              onKeyDown={handleFirstInputKeyDown}
            />
          ) : (
            <span
              className="text-xs text-muted-foreground mt-1 cursor-pointer hover:underline"
              tabIndex={0}
              onClick={handleFirstSpanClick}
              onFocus={handleFirstSpanClick}
            >
              {formatNumber(firstValue)}
            </span>
          )}
        </div>
        <span className="mx-2 text-muted-foreground">-</span>
        <div className="flex flex-col items-center">
          {editLast ? (
            <FormInput
              type="number"
              className="text-xs text-muted-foreground mt-1 border rounded px-1 py-0.5 w-20"
              value={lastInput}
              autoFocus
              min={firstValue}
              max={max}
              step={step}
              onChange={handleLastInputChange}
              onBlur={handleLastBlur}
              onKeyDown={handleLastInputKeyDown}
            />
          ) : (
            <span
              className="text-xs text-muted-foreground mt-1 cursor-pointer hover:underline"
              tabIndex={0}
              onClick={handleLastSpanClick}
              onFocus={handleLastSpanClick}
            >
              {formatNumber(lastValue)}
            </span>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

export {
  Form,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  FormCheckbox,
  FormActions,
  FormSlider,
};
